/**
 * Shared SMS thread helpers — RingCentral-grade bubble polish reused by
 * BOTH the Ring Center Text tab (pages/ring/RingWorkspace) and the standalone
 * SMS page (components/ReceptionistSMSPanel).
 *   • dayLabel()      → "Today" / "Yesterday" / "Mon, Jun 3" date-separator text
 *   • DeliveryTicks   → outbound delivery state (pending / sent / delivered / failed)
 */
import { Check, CheckCheck, Clock, AlertCircle } from "lucide-react";

export function dayLabel(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  if (isNaN(d.getTime())) return "";
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return "Today";
  const y = new Date(now);
  y.setDate(now.getDate() - 1);
  if (d.toDateString() === y.toDateString()) return "Yesterday";
  const sameYear = d.getFullYear() === now.getFullYear();
  return d.toLocaleDateString([], {
    weekday: "short",
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" }),
  });
}

// Map a message's Twilio delivery state (delivery_status preferred, status
// fallback) to one of: failed / delivered / pending / sent.
export function deliveryKind(m) {
  const raw = String(m?.delivery_status || m?.status || "").toLowerCase();
  if (raw === "failed" || raw === "undelivered") return "failed";
  if (raw === "delivered" || raw === "read") return "delivered";
  if (raw === "queued" || raw === "accepted" || raw === "sending" || raw === "scheduled") return "pending";
  return "sent";
}

// Small read-receipt style ticks shown next to an outbound bubble's time.
// `light` = rendered on a dark (navy) bubble.
export function DeliveryTicks({ m, light }) {
  const kind = deliveryKind(m);
  const dim = light ? "rgba(255,255,255,0.65)" : "#94A3B8";
  if (kind === "failed") {
    return (
      <span title="Not delivered" className="inline-flex items-center" data-testid="sms-tick-failed">
        <AlertCircle size={12} color="#F87171" />
      </span>
    );
  }
  if (kind === "pending") {
    return (
      <span title="Sending…" className="inline-flex items-center" data-testid="sms-tick-pending">
        <Clock size={11} color={dim} />
      </span>
    );
  }
  if (kind === "delivered") {
    return (
      <span title="Delivered" className="inline-flex items-center" data-testid="sms-tick-delivered">
        <CheckCheck size={13} color={light ? "#8FE3FF" : "#1D9E75"} />
      </span>
    );
  }
  return (
    <span title="Sent" className="inline-flex items-center" data-testid="sms-tick-sent">
      <Check size={12} color={dim} />
    </span>
  );
}
